import React, { useMemo, useState } from 'react';
import { FormControlLabel, Checkbox, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import FlightService from '../../../services/FlightService';

type LightSpeedRJobSettingsProps = {
  historySelected: boolean;
  options: { skipInventory?: boolean; loadFullInventory?: boolean; sinceLastRun?: boolean; grabAllOnOrder?: boolean };
  onChange: (next: { skipInventory?: boolean; loadFullInventory?: boolean; sinceLastRun?: boolean; grabAllOnOrder?: boolean }) => void;
  client: any;
  tipContext?: any;
};

type AdvancedState = {
  open: boolean;
  running: boolean;
  period: Moment;
  steps: {
    products: boolean;
    inventory: boolean;
    orders: boolean;
    transfers: boolean;
    purchaseOrders: boolean;
  };
  merge: {
    actuals: boolean;
    includeHardMarkdowns: boolean;
    onOrder: boolean;
    receiving: boolean;
  };
  error: string;
};

type AdvancedSettingsState = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  error: string;
  autoMergeActuals: boolean;
  apiRefreshToken: string;
  apiKey: string;
};

const defaultAdvancedState = (): AdvancedState => ({
  open: false,
  running: false,
  period: moment(),
  steps: {
    products: true,
    inventory: true,
    orders: true,
    transfers: true,
    purchaseOrders: true,
  },
  merge: {
    actuals: false,
    includeHardMarkdowns: false,
    onOrder: false,
    receiving: false,
  },
  error: '',
});

const defaultAdvancedSettingsState = (): AdvancedSettingsState => ({
  open: false,
  loading: false,
  saving: false,
  error: '',
  autoMergeActuals: false,
  apiRefreshToken: '',
  apiKey: '',
});

const LightSpeedRJobSettings: React.FC<LightSpeedRJobSettingsProps> = ({
  historySelected,
  options,
  onChange,
  client,
  tipContext,
}) => {
  const loadFullInventorySelected = !!options.loadFullInventory;
  const loadFullOnOrderSelected = !!options.grabAllOnOrder;

  const onToggleLoadFullInventory = (checked: boolean) => {
    if (checked) {
      onChange({
        ...options,
        loadFullInventory: true,
        grabAllOnOrder: false,
        skipInventory: false,
        sinceLastRun: false,
      });
      return;
    }

    onChange({ ...options, loadFullInventory: false });
  };

  const onToggleLoadFullOnOrder = (checked: boolean) => {
    if (checked) {
      onChange({
        ...options,
        grabAllOnOrder: true,
        loadFullInventory: false,
        skipInventory: false,
        sinceLastRun: false,
      });
      return;
    }

    onChange({ ...options, grabAllOnOrder: false });
  };

  const [advanced, setAdvanced] = useState<AdvancedState>(defaultAdvancedState());
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettingsState>(defaultAdvancedSettingsState());

  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [mergeModeHistory, setMergeModeHistory] = useState(false);
  const [mergeActuals, setMergeActuals] = useState(true);
  const [mergeIncludeHardMarkdowns, setMergeIncludeHardMarkdowns] = useState(false);
  const [mergeOnOrder, setMergeOnOrder] = useState(false);
  const [mergeReceiving, setMergeReceiving] = useState(false);
  const [mergeStartMonth, setMergeStartMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));
  const [mergeEndMonth, setMergeEndMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));

  const isCurrentMonth = useMemo(
    () => advanced.period.isSame(moment(), 'month'),
    [advanced.period]
  );

  const patchAdvanced = (next: Partial<AdvancedState>) => {
    setAdvanced((prev) => ({ ...prev, ...next }));
  };

  const patchSteps = (next: Partial<AdvancedState['steps']>) => {
    setAdvanced((prev) => ({ ...prev, steps: { ...prev.steps, ...next } }));
  };

  const patchMerge = (next: Partial<AdvancedState['merge']>) => {
    setAdvanced((prev) => ({ ...prev, merge: { ...prev.merge, ...next } }));
  };

  const patchAdvancedSettings = (next: Partial<AdvancedSettingsState>) => {
    setAdvancedSettings((prev) => ({ ...prev, ...next }));
  };

  const runMerge = async (allowOnOrder: boolean, startDate?: string, endDate?: string) => {
    const mergeOptions = {
      actuals: mergeActuals,
      includeHardMarkdowns: mergeIncludeHardMarkdowns,
      onOrder: mergeOnOrder && allowOnOrder,
      receiving: mergeReceiving,
    };
    await FlightService.mergeLightSpeedR(client.Id, mergeOptions, { startDate, endDate });
  };

  const onMergeActuals = async () => {
    if (mergeRunning) return;
    setMergeError('');

    if (!client?.Id) {
      setMergeError('Missing client id');
      return;
    }

    if (!mergeActuals && !mergeOnOrder && !mergeReceiving) {
      setMergeError('Please select at least one merge operation.');
      return;
    }

    try {
      setMergeRunning(true);

      if (mergeModeHistory) {
        if (!mergeStartMonth || !mergeEndMonth) {
          setMergeError('Please select a start and end month.');
          setMergeRunning(false);
          return;
        }

        const start = mergeStartMonth.clone().startOf('month');
        const end = mergeEndMonth.clone().startOf('month');
        if (start.isAfter(end)) {
          setMergeError('Start month must be on or before end month.');
          setMergeRunning(false);
          return;
        }

        const startDate = start.format('YYYY-MM-DD');
        const endDate = end.endOf('month').format('YYYY-MM-DD');

        tipContext?.showSuccess?.('Merging LightSpeed R data for selected history months. This may take several minutes...', 10);
        // History mode runs as one merge request for the full selected range.
        await runMerge(false, startDate, endDate);
      } else {
        const now = moment();
        const startDate = now.clone().startOf('month').format('YYYY-MM-DD');
        const endDate = now.clone().endOf('month').format('YYYY-MM-DD');
        tipContext?.showSuccess?.('Merging LightSpeed R data for the current month. This may take several minutes...', 10);
        await runMerge(true, startDate, endDate);
      }

      tipContext?.showSuccess?.('LightSpeed R merges completed.', 10);
      setMergeModalOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Merge failed';
      setMergeError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setMergeRunning(false);
    }
  };

  const onAdvancedRun = async () => {
    if (advanced.running) return;
    patchAdvanced({ error: '' });

    if (!client?.Id) {
      patchAdvanced({ error: 'Missing client id.' });
      return;
    }

    const steps: string[] = [];
    if (advanced.steps.products) steps.push('products');
    if (advanced.steps.inventory) steps.push('inventory');
    if (advanced.steps.orders) steps.push('orders');
    if (advanced.steps.transfers) steps.push('transfers');
    if (advanced.steps.purchaseOrders) steps.push('purchase_orders');

    const mergeOptions = {
      actuals: advanced.merge.actuals,
      includeHardMarkdowns: advanced.merge.includeHardMarkdowns,
      onOrder: advanced.merge.onOrder && isCurrentMonth,
      receiving: advanced.merge.receiving,
    };

    const hasMerge =
      mergeOptions.actuals || mergeOptions.onOrder || mergeOptions.receiving;

    if (steps.length === 0 && !hasMerge) {
      patchAdvanced({ error: 'Please select at least one step or merge operation.' });
      return;
    }

    try {
      patchAdvanced({ running: true });
      tipContext?.showSuccess?.('Starting LightSpeed R advanced run...', 8);

      await FlightService.runLightSpeedRAdvanced(
        client.Id,
        advanced.period.format('YYYYMM'),
        steps,
        mergeOptions
      );

      tipContext?.showSuccess?.('LightSpeed R advanced run completed.', 10);
      patchAdvanced({ open: false });
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'LightSpeed R advanced run failed.';
      patchAdvanced({ error: msg });
      tipContext?.show?.(msg, 12);
    } finally {
      patchAdvanced({ running: false });
    }
  };

  const openAdvancedSettings = async () => {
    if (!client?.Id || advancedSettings.loading) return;

    patchAdvancedSettings({ open: true, loading: true, error: '' });

    try {
      const res = await FlightService.getLightSpeedRAdvancedSettings(client.Id);
      const data = res?.result ?? res;
      patchAdvancedSettings({
        autoMergeActuals: !!data?.auto_merge_actuals,
        apiRefreshToken: data?.api_refresh_token ?? '',
        apiKey: data?.api_key ?? '',
      });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to load advanced settings';
      patchAdvancedSettings({ error: msg });
    } finally {
      patchAdvancedSettings({ loading: false });
    }
  };

  const onSaveAdvancedSettings = async () => {
    if (!client?.Id || advancedSettings.saving) return;

    patchAdvancedSettings({ error: '' });

    try {
      patchAdvancedSettings({ saving: true });
      await FlightService.updateLightSpeedRAdvancedSettings(client.Id, {
        auto_merge_actuals: advancedSettings.autoMergeActuals,
        api_refresh_token: advancedSettings.apiRefreshToken.trim(),
        api_key: advancedSettings.apiKey.trim(),
      });
      tipContext?.showSuccess?.('Advanced settings saved.', 8);
      patchAdvancedSettings({ open: false });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to save advanced settings';
      patchAdvancedSettings({ error: msg });
      tipContext?.show?.(msg, 12);
    } finally {
      patchAdvancedSettings({ saving: false });
    }
  };

  return (
    <>
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.skipInventory}
            disabled={loadFullInventorySelected || loadFullOnOrderSelected}
            onChange={(e) => onChange({ ...options, skipInventory: e.target.checked })}
            color="primary"
          />
        }
        label="Skip inventory load?"
      />
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.loadFullInventory}
            disabled={!!historySelected || loadFullOnOrderSelected}
            onChange={(e) => onToggleLoadFullInventory(e.target.checked)}
            color="primary"
          />
        }
        label="Load full inventory? (first time loads)"
      />
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.sinceLastRun}
            disabled={!!historySelected || loadFullInventorySelected || loadFullOnOrderSelected}
            onChange={(e) => onChange({ ...options, sinceLastRun: e.target.checked })}
            color="primary"
          />
        }
        label="Since last run only (delta)"
      />
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.grabAllOnOrder}
            disabled={!!historySelected || loadFullInventorySelected}
            onChange={(e) => onToggleLoadFullOnOrder(e.target.checked)}
            color="primary"
          />
        }
        label="Load full on order? (past 2 years, on-order only)"
      />

      <div className="mt-3 border-t border-gray-300 pt-3 px-1">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={() => setMergeModalOpen(true)}
            disabled={mergeRunning}
          >
            Merge Actuals
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
            onClick={openAdvancedSettings}
            disabled={advancedSettings.loading || advancedSettings.saving}
          >
            Advanced Settings
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-blue-400 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
            onClick={() => patchAdvanced({ open: true, error: '' })}
            disabled={advanced.running}
          >
            Advanced Run
          </button>
        </div>
      </div>

      {mergeModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!mergeRunning ? setMergeModalOpen(false) : undefined)} />

          <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">Merge LightSpeed R Data</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => setMergeModalOpen(false)}
                disabled={mergeRunning}
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-sm text-gray-800">
              Select which merge operations to run. This may take several minutes — you may use RO on other tabs without issue.
            </div>

            {/* Mode toggle */}
            <div className="mt-4 flex rounded-md border border-gray-300 overflow-hidden w-fit">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  !mergeModeHistory
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } disabled:opacity-50`}
                onClick={() => setMergeModeHistory(false)}
                disabled={mergeRunning}
              >
                Current Month
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                  mergeModeHistory
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } disabled:opacity-50`}
                onClick={() => setMergeModeHistory(true)}
                disabled={mergeRunning}
              >
                History
              </button>
            </div>

            {/* History date range — only shown in history mode */}
            {mergeModeHistory ? (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Start Month</label>
                    <DatePicker
                      views={['year', 'month']}
                      label="Start Month"
                      minDate={moment('2000-01-01')}
                      maxDate={moment().subtract(1, 'month')}
                      value={mergeStartMonth}
                      onChange={(newValue: Moment | null) => {
                        if (!newValue) return;
                        setMergeStartMonth(newValue);
                      }}
                      disabled={mergeRunning}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      inputFormat="MMM YYYY"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">End Month</label>
                    <DatePicker
                      views={['year', 'month']}
                      label="End Month"
                      minDate={moment('2000-01-01')}
                      maxDate={moment().subtract(1, 'month')}
                      value={mergeEndMonth}
                      onChange={(newValue: Moment | null) => {
                        if (!newValue) return;
                        setMergeEndMonth(newValue);
                      }}
                      disabled={mergeRunning}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      inputFormat="MMM YYYY"
                    />
                  </div>
                </LocalizationProvider>
              </div>
            ) : null}

            {/* Merge type checkboxes */}
            <div className="mt-4 space-y-1">
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mergeActuals}
                      onChange={(e) => setMergeActuals(e.target.checked)}
                      disabled={mergeRunning}
                    />
                  }
                  label={<span className="text-sm">Merge actuals</span>}
                />
                {mergeActuals ? (
                  <div style={{ marginLeft: 32 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={mergeIncludeHardMarkdowns}
                          onChange={(e) => setMergeIncludeHardMarkdowns(e.target.checked)}
                          disabled={mergeRunning}
                        />
                      }
                      label={<span className="text-sm">Include hard markdowns</span>}
                    />
                  </div>
                ) : null}
              </div>

              {mergeActuals && mergeModeHistory ? (
                <div className="ml-8 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠️ Inventory values are NOT updated for historical months. LightSpeed R has no
                  point-in-time inventory API, so historical merges leave existing
                  inventory/inventory cost values untouched.
                </div>
              ) : null}

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mergeOnOrder}
                    onChange={(e) => setMergeOnOrder(e.target.checked)}
                    disabled={mergeRunning || mergeModeHistory}
                  />
                }
                label={<span className="text-sm">Merge on orders {' — '}<span className="text-xs text-gray-400">current month only</span></span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mergeReceiving}
                    onChange={(e) => setMergeReceiving(e.target.checked)}
                    disabled={mergeRunning}
                  />
                }
                label={<span className="text-sm">Merge receiving</span>}
              />

              {mergeOnOrder && mergeModeHistory ? (
                <div className="ml-8 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠️ Merge on orders is only available for current month.
                </div>
              ) : null}
            </div>

            {mergeRunning ? (
              <div className="mt-4 text-sm text-gray-600">
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                  Running merges...
                </span>
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={mergeRunning || (mergeModeHistory && (!mergeStartMonth || !mergeEndMonth))}
                onClick={onMergeActuals}
              >
                {mergeRunning ? 'Running...' : 'Run'}
              </button>
            </div>

            {mergeError ? <div className="mt-2 text-sm text-red-600">{mergeError}</div> : null}
          </div>
        </div>
      ) : null}

      {advancedSettings.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!advancedSettings.saving ? patchAdvancedSettings({ open: false }) : undefined)} />

          <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">LightSpeed R Advanced Settings</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => patchAdvancedSettings({ open: false })}
                disabled={advancedSettings.saving}
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-sm text-gray-700">
              Update auto-merge behavior and stored LightSpeed R API credentials.
            </div>

            {advancedSettings.loading ? (
              <div className="mt-4 text-sm text-gray-600">Loading settings...</div>
            ) : (
              <>
                <div className="mt-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={advancedSettings.autoMergeActuals}
                        onChange={(e) => patchAdvancedSettings({ autoMergeActuals: e.target.checked })}
                        disabled={advancedSettings.saving}
                      />
                    }
                    label={<span className="text-sm">Auto merge actuals</span>}
                  />
                </div>

                <div className="mt-3 space-y-3">
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    These credentials are stored directly on the client record. Use Client auth from the Client Profile page instead where possible, and only set keys here when that flow is not available.
                  </div>
                  <TextField
                    fullWidth
                    size="small"
                    label="API Refresh Token"
                    value={advancedSettings.apiRefreshToken}
                    onChange={(e) => patchAdvancedSettings({ apiRefreshToken: e.target.value })}
                    disabled={advancedSettings.saving}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="API Key"
                    value={advancedSettings.apiKey}
                    onChange={(e) => patchAdvancedSettings({ apiKey: e.target.value })}
                    disabled={advancedSettings.saving}
                  />
                </div>
              </>
            )}

            {advancedSettings.error ? (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {advancedSettings.error}
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => patchAdvancedSettings({ open: false })}
                disabled={advancedSettings.saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-blue-500 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={onSaveAdvancedSettings}
                disabled={advancedSettings.loading || advancedSettings.saving}
              >
                {advancedSettings.saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {advanced.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!advanced.running ? patchAdvanced({ open: false }) : undefined)} />

          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">LightSpeed R Advanced Run</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => patchAdvanced({ open: false })}
                disabled={advanced.running}
              >
                Close
              </button>
            </div>

            <div className="mt-2 text-sm text-gray-700">
              Select which data steps to run and optionally merge after. Only the chosen steps will execute.
            </div>

            <div className="mt-4">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  views={['year', 'month']}
                  label="Period"
                  minDate={moment('2000-01-01')}
                  maxDate={moment()}
                  value={advanced.period}
                  onChange={(newValue: Moment | null) => {
                    if (!newValue) return;
                    patchAdvanced({ period: newValue });
                  }}
                  disabled={advanced.running}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  inputFormat="MMM YYYY"
                />
              </LocalizationProvider>
            </div>

            <div className="mt-4 space-y-1">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Steps</div>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.steps.products}
                    onChange={(e) => patchSteps({ products: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Products</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.steps.inventory}
                    onChange={(e) => patchSteps({ inventory: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Inventory</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.steps.orders}
                    onChange={(e) => patchSteps({ orders: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Orders</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.steps.transfers}
                    onChange={(e) => patchSteps({ transfers: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Transfers</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.steps.purchaseOrders}
                    onChange={(e) => patchSteps({ purchaseOrders: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Purchase Orders</span>}
              />
            </div>

            <div className="mt-4 space-y-1">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Merge Operations</div>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.merge.actuals}
                    onChange={(e) => patchMerge({ actuals: e.target.checked, includeHardMarkdowns: e.target.checked ? advanced.merge.includeHardMarkdowns : false })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Merge Actuals</span>}
              />
              {advanced.merge.actuals ? (
                <div className="ml-6">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advanced.merge.includeHardMarkdowns}
                        onChange={(e) => patchMerge({ includeHardMarkdowns: e.target.checked })}
                        disabled={advanced.running}
                      />
                    }
                    label={<span className="text-sm">Include Hard Markdowns</span>}
                  />
                </div>
              ) : null}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.merge.onOrder}
                    onChange={(e) => patchMerge({ onOrder: e.target.checked })}
                    disabled={advanced.running || !isCurrentMonth}
                  />
                }
                label={
                  <span className="text-sm">
                    Merge On Order
                    {!isCurrentMonth ? <span className="ml-1 text-xs text-gray-400">(current month only)</span> : null}
                  </span>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={advanced.merge.receiving}
                    onChange={(e) => patchMerge({ receiving: e.target.checked })}
                    disabled={advanced.running}
                  />
                }
                label={<span className="text-sm">Merge Receiving</span>}
              />
            </div>

            {advanced.error ? (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {advanced.error}
              </div>
            ) : null}

            {advanced.running ? (
              <div className="mt-4 text-sm text-gray-600">
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                  Running...
                </span>
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => patchAdvanced({ open: false })}
                disabled={advanced.running}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-blue-500 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={onAdvancedRun}
                disabled={advanced.running}
              >
                {advanced.running ? 'Running...' : 'Run Advanced'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default LightSpeedRJobSettings;
